// CRAP is inflated only when the CI fallow coverage step times out (0% scoring, see
// .github/workflows/fallow.yml); these branchy fns are fully unit-tested. Drop when fixed.
// fallow-ignore-file complexity
// @plasser/ui — QuickCreate inline mini-form rendered inside the picker.
// "not found → + Neu → inline form → auto-select, no navigation away".

import * as React from "react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { cn } from "../lib/utils";
import type { EntityRecord, EntityTypeConfig, SearchProvider } from "./types";

export interface QuickCreateProps {
  config: EntityTypeConfig;
  provider: SearchProvider;
  /** Prefill for the first text field (usually the current search text). */
  initialName?: string;
  /** Called with the created record so the picker can auto-select it. */
  onCreated: (record: EntityRecord) => void;
  onCancel: () => void;
  /** Button copy (defaults are German). */
  cancelLabel?: string;
  submitLabel?: string;
  submittingLabel?: string;
  className?: string;
}

export function QuickCreate({
  config,
  provider,
  initialName = "",
  onCreated,
  onCancel,
  cancelLabel = "Abbrechen",
  submitLabel = "Anlegen",
  submittingLabel = "Wird angelegt…",
  className,
}: QuickCreateProps): React.ReactElement | null {
  const qc = config.quickCreate;
  const firstKey = qc?.fields[0]?.key ?? "name";
  const [values, setValues] = React.useState<Record<string, string>>(() => ({
    [firstKey]: initialName,
  }));
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  if (!qc || !provider.create) return null;

  const setField = (key: string, value: string) => setValues((prev) => ({ ...prev, [key]: value }));

  const missingRequired = qc.fields.some((f) => f.required && !(values[f.key] ?? "").trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || missingRequired) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await provider.create!(config, values);
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="quick-create-form"
      className={cn(
        "flex flex-col gap-3 rounded-md border border-border bg-muted/30 p-3",
        className,
      )}
    >
      <p className="text-sm font-medium text-foreground">{qc.label}</p>
      {qc.fields.map((field) => (
        <div key={field.key} className="flex flex-col gap-1">
          <Label htmlFor={`qc-${field.key}`} className="text-xs">
            {field.label}
            {field.required ? <span className="text-destructive"> *</span> : null}
          </Label>
          {field.type === "textarea" ? (
            <Textarea
              id={`qc-${field.key}`}
              data-testid={`quick-create-${field.key}`}
              value={values[field.key] ?? ""}
              placeholder={field.placeholder}
              onChange={(e) => setField(field.key, e.target.value)}
            />
          ) : (
            <Input
              id={`qc-${field.key}`}
              data-testid={`quick-create-${field.key}`}
              value={values[field.key] ?? ""}
              placeholder={field.placeholder}
              onChange={(e) => setField(field.key, e.target.value)}
            />
          )}
        </div>
      ))}
      {error ? (
        <p role="alert" className="text-xs text-destructive" data-testid="quick-create-error">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={submitting}>
          {cancelLabel}
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={submitting || missingRequired}
          data-testid="quick-create-submit"
        >
          {submitting ? submittingLabel : submitLabel}
        </Button>
      </div>
    </form>
  );
}
